import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import api, { fmtEUR, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Copy, RefreshCw, Paperclip, Download } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useSearchParams } from "react-router-dom";
import { monthOptions, monthRangeFor } from "@/lib/utils";
import { toast } from "sonner";
import { STATUS, PAYMENT, ConfBadge, InvSortHead } from "@/components/facturas/shared";
import { DuplicatesDialog } from "@/components/facturas/DuplicatesDialog";
import { AttachPageDialog } from "@/components/facturas/AttachPageDialog";
import { InvoiceDrawer } from "@/components/facturas/InvoiceDrawer";

const NUM_KEYS = ["base_amount", "vat_amount", "total", "overall_confidence"];

const fmtPct = (r) => `${Number.isInteger(r) ? r : r.toFixed(1)}%`;

export function VatRatesCell({ inv }) {
  const vs = inv.vat_summary || { rates: [] };
  if (!vs.rates?.length) return <span className="text-xs text-slate-400">—</span>;
  if (vs.rates.length === 1) {
    return (
      <span className="font-num text-xs"
        title={vs.estimated ? "Tipo efectivo estimado a partir de la base y el IVA totales del documento" : undefined}>
        {fmtPct(vs.rates[0])}{vs.estimated ? "*" : ""}
      </span>
    );
  }
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid={`invoice-vat-expand-${inv.id}`}
          className="font-num text-xs text-blue-700 underline decoration-dotted hover:text-blue-900"
          onClick={(e) => e.stopPropagation()}>
          {vs.rates.map(fmtPct).join(" + ")}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72" onClick={(e) => e.stopPropagation()}>
        <div className="text-xs font-semibold mb-2">Desglose de IVA por tipo</div>
        {(vs.breakdown || []).map((b) => (
          <div key={b.rate} className="flex justify-between text-xs py-0.5">
            <span>IVA {fmtPct(b.rate)}</span>
            <span className="font-num">base {fmtEUR(b.base)} · cuota {fmtEUR(b.cuota)}</span>
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export default function Facturas() {
  const { branchFilter, org, user } = useAuth();
  const isAdmin = user?.role === "ADMIN";
  const canReconcile = ["ADMIN", "FINANCE"].includes(user?.role);
  const [invoices, setInvoices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dupCount, setDupCount] = useState(0);
  const [invSort, setInvSort] = useState({ key: "detected_at", dir: "desc" });
  const [scanning, setScanning] = useState(false);
  const [filters, setFilters] = useState({ status: "all", payment_status: "all", source: "all", branch: "", date_from: "", date_to: "", q: "", contabilizada: "all", currency: "all", registro_from: "", registro_to: "" });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [zipping, setZipping] = useState(false);
  const monthSel = filters.date_from && filters.date_to
    && monthRangeFor(filters.date_from.slice(0, 7))[0] === filters.date_from
    && monthRangeFor(filters.date_from.slice(0, 7))[1] === filters.date_to
    ? filters.date_from.slice(0, 7) : "all";

  const clickInvSort = (key, defDir) =>
    setInvSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: defDir }));
  const syncDrive = async () => {
    try {
      const r = await api.post("/drive/scan");
      toast.success(r.data.already_running
        ? "Ya hay un escaneo en curso"
        : "Sincronización iniciada — las facturas nuevas irán apareciendo");
      setScanning(true);
      const poll = setInterval(async () => {
        const s = await api.get("/drive/status").then((r2) => r2.data).catch(() => null);
        if (s && !s.scanning) {
          clearInterval(poll);
          setScanning(false);
          load();
          toast.success("Sincronización de carpetas completada");
        }
      }, 4000);
      setTimeout(() => { clearInterval(poll); setScanning(false); }, 10 * 60 * 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al sincronizar — revisa la conexión de Drive en Configuración");
    }
  };

  const sortedInvoices = [...invoices].sort((a, b) => {
    const k = invSort.key;
    let cmp;
    if (NUM_KEYS.includes(k)) cmp = (a[k] ?? -Infinity) - (b[k] ?? -Infinity);
    else cmp = String(a[k] ?? "").localeCompare(String(b[k] ?? ""), "es", { sensitivity: "base", numeric: true });
    return invSort.dir === "asc" ? cmp : -cmp;
  });
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [payments, setPayments] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const focusInvoice = searchParams.get("invoice");
  const focusInvoiceRef = useRef(null);
  const [invCandidates, setInvCandidates] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {}); }, []);
  const expenseCategories = useMemo(() => categories.filter((c) => c.group !== "otros_ingresos"), [categories]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [page2Url, setPage2Url] = useState(null);
  const [activePage, setActivePage] = useState(1);
  const [attachOpen, setAttachOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showDups, setShowDups] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (filters.status !== "all") params.set("status", filters.status);
    if (filters.payment_status !== "all") params.set("payment_status", filters.payment_status);
    if (filters.source !== "all") params.set("source", filters.source);
    const branch = filters.branch || branchFilter;
    if (branch) params.set("branch_id", branch);
    if (filters.date_from) params.set("date_from", filters.date_from);
    if (filters.date_to) params.set("date_to", filters.date_to);
    if (filters.q) params.set("q", filters.q);
    if (filters.contabilizada !== "all") params.set("contabilizada", filters.contabilizada);
    if (filters.currency !== "all") params.set("currency", filters.currency);
    if (filters.registro_from) params.set("registro_from", filters.registro_from);
    if (filters.registro_to) params.set("registro_to", filters.registro_to);
    api.get(`/invoices?${params}`).then((r) => setInvoices(r.data)).catch(() => {});
    api.get("/invoices/duplicates").then((r) => setDupCount(r.data.length)).catch(() => {});
  }, [filters, branchFilter]);

  useEffect(() => { load(); }, [load]);

  const exportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status !== "all") params.set("status", filters.status);
      if (filters.payment_status !== "all") params.set("payment_status", filters.payment_status);
      if (filters.source !== "all") params.set("source", filters.source);
      const branch = filters.branch || branchFilter;
      if (branch) params.set("branch_id", branch);
      if (filters.date_from) params.set("date_from", filters.date_from);
      if (filters.date_to) params.set("date_to", filters.date_to);
      if (filters.q) params.set("q", filters.q);
      if (filters.contabilizada !== "all") params.set("contabilizada", filters.contabilizada);
      if (filters.currency !== "all") params.set("currency", filters.currency);
      if (filters.registro_from) params.set("registro_from", filters.registro_from);
      if (filters.registro_to) params.set("registro_to", filters.registro_to);
      const r = await api.get(`/invoices/export?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `facturas_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch {
      toast.error("Error al exportar el listado");
    }
  };
  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)).catch(() => {}); }, []);

  // Deep link: /facturas?invoice=<id> → abre directamente el drawer de la factura
  useEffect(() => {
    if (!focusInvoice || focusInvoiceRef.current === focusInvoice) return;
    focusInvoiceRef.current = focusInvoice;
    openDetail({ id: focusInvoice });
    setSearchParams({}, { replace: true });
  }, [focusInvoice]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (branchFilter) fd.append("branch_id", branchFilter);
      const r = await api.post("/invoices/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      const st = r.data.status;
      if (st === "validated") toast.success("Factura procesada y validada");
      else if (st === "duplicate") toast.warning("Factura detectada como posible duplicado");
      else if (st === "error") toast.error("Error OCR — enviada a bandeja de revisión");
      else toast.info("Factura procesada — pendiente de revisión");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir la factura");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const openDetail = async (inv) => {
    let r;
    try {
      r = await api.get(`/invoices/${inv.id}`);
    } catch (e) {
      toast.error(e.response?.status === 404 ? "Factura no encontrada" : "Error al cargar la factura");
      return;
    }
    setSelected(r.data);
    if (r.data.payment_status !== "pagada" && (r.data.total || 0) - (r.data.paid_amount || 0) > 0.005) {
      api.get(`/reconciliation/for-invoice/${r.data.id}`).then((rr) => setInvCandidates(rr.data)).catch(() => setInvCandidates(null));
    } else {
      setInvCandidates(null);
    }
    api.get(`/payables/${inv.id}/payments`).then((r2) => setPayments(r2.data)).catch(() => setPayments([]));
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    if (page2Url) URL.revokeObjectURL(page2Url);
    setPreviewUrl(null);
    setPage2Url(null);
    setActivePage(1);
    if (r.data.storage_path) {
      api.get(`/invoices/${inv.id}/file`, { responseType: "blob" })
        .then((r3) => setPreviewUrl(URL.createObjectURL(r3.data)))
        .catch(() => setPreviewUrl(null));
    }
    setEditForm({
      invoice_number: r.data.invoice_number || "", invoice_date: r.data.invoice_date || "",
      due_date: r.data.due_date || "", base_amount: r.data.base_amount ?? "", vat_amount: r.data.vat_amount ?? "",
      other_taxes: r.data.other_taxes ?? "", total: r.data.total ?? "", branch_id: r.data.branch_id || "",
      category_id: r.data.category_id || "", payment_status: r.data.payment_status || "pendiente",
      paid_amount: r.data.paid_amount ?? 0, doc_kind: r.data.doc_kind || "ticket",
    });
  };

  const closeDrawer = () => {
    setSelected(null);
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    if (page2Url) { URL.revokeObjectURL(page2Url); setPage2Url(null); }
    setActivePage(1);
    setPayments([]);
    setInvCandidates(null);
  };

  const reconcileFromInvoice = async (txId) => {
    try {
      const pending = Math.round(((selected.total || 0) - (selected.paid_amount || 0)) * 100) / 100;
      await api.post("/reconciliation/manual", { transaction_id: txId, allocations: [{ invoice_id: selected.id, amount: pending }] });
      toast.success("Factura conciliada con el movimiento");
      setInvCandidates(null);
      openDetail(selected);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al conciliar");
    }
  };

  const saveEdit = async () => {
    try {
      const payload = { ...editForm };
      ["base_amount", "vat_amount", "other_taxes", "total", "paid_amount"].forEach((k) => {
        payload[k] = payload[k] === "" ? null : Number(payload[k]);
      });
      if (payload.payment_status === "pagada" && !payload.paid_amount) {
        payload.paid_amount = payload.total || 0;
      }
      payload.branch_id = payload.branch_id || null;
      payload.category_id = payload.category_id || null;
      await api.patch(`/invoices/${selected.id}`, payload);
      toast.success("Factura actualizada");
      setSelected(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar (solo ADMIN puede editar)");
    }
  };

  const approve = async () => {
    await api.post(`/invoices/${selected.id}/approve`);
    toast.success("Factura validada");
    setSelected(null);
    load();
  };

  const remove = async () => {
    if (!window.confirm("¿Eliminar esta factura? (borrado lógico, queda auditado)")) return;
    await api.delete(`/invoices/${selected.id}`);
    toast.success("Factura eliminada (borrado lógico)");
    setSelected(null);
    load();
  };

  const openPage2 = async () => {
    setActivePage(2);
    if (page2Url || !selected?.attached_page) return;
    try {
      const r = await api.get(`/invoices/${selected.attached_page.id}/file`, { responseType: "blob" });
      setPage2Url(URL.createObjectURL(r.data));
    } catch {
      toast.error("No se pudo cargar la página 2");
    }
  };

  const detachPage = async () => {
    if (!window.confirm("¿Desasociar la página 2? Volverá a ser una factura independiente (gasto propio).")) return;
    try {
      await api.post(`/invoices/${selected.id}/detach-page`);
      toast.success("Página desasociada — vuelve a ser factura independiente");
      openDetail(selected);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al desasociar");
    }
  };

  const setF = (k, v) => setFilters({ ...filters, [k]: v });
  const canGestoria = ["ADMIN", "FINANCE"].includes(user?.role);
  const fmtRegistro = (iso) => (iso
    ? new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "—");

  const toggleSelect = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAllFiltered = () => setSelectedIds((prev) => (
    sortedInvoices.every((i) => prev.has(i.id)) && sortedInvoices.length > 0
      ? new Set()
      : new Set(sortedInvoices.map((i) => i.id))));

  const toggleContabilizada = async (inv) => {
    try {
      await api.patch(`/invoices/${inv.id}/gestoria`, { contabilizada: !inv.contabilizada });
      toast.success(inv.contabilizada ? "Marca de contabilizada quitada" : "Marcada como contabilizada");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al actualizar");
    }
  };

  const markBulkContabilizadas = async () => {
    try {
      const r = await api.post("/invoices/contabilizada-bulk", { ids: [...selectedIds], contabilizada: true });
      toast.success(`${r.data.updated} facturas marcadas como contabilizadas`);
      setSelectedIds(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al marcar");
    }
  };

  const downloadZip = async () => {
    if (!selectedIds.size || zipping) return;
    setZipping(true);
    try {
      const r = await api.post("/invoices/download-zip", { ids: [...selectedIds] },
        { responseType: "blob", timeout: 300000 });
      const missing = Number(r.headers?.["x-missing-count"] || 0);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `facturas_${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      if (missing > 0) toast.warning(`ZIP descargado — ${missing} sin documento (ver FALTAN_PDF.txt)`);
      else toast.success("ZIP descargado");
    } catch {
      toast.error("Error al generar el ZIP");
    } finally {
      setZipping(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="facturas-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Facturas</h1>
          <p className="text-sm text-slate-500 mt-1">OCR automático · antiduplicados · dudas a bandeja de revisión</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="duplicates-button" variant="outline" onClick={() => setShowDups(true)} className="gap-2">
            <Copy className="w-4 h-4" /> Duplicadas
            {dupCount > 0 && <span className="bg-red-100 text-red-700 text-xs font-bold px-1.5 py-0.5 rounded-full">{dupCount}</span>}
          </Button>
          <Button data-testid="export-invoices-button" variant="outline" onClick={exportExcel} className="gap-2">
            <Download className="w-4 h-4" /> Excel
          </Button>
          <Button data-testid="sync-drive-button" variant="outline" onClick={syncDrive} disabled={scanning} className="gap-2">
            <RefreshCw className={`w-4 h-4 ${scanning ? "animate-spin" : ""}`} /> {scanning ? "Sincronizando..." : "Sincronizar carpetas"}
          </Button>
          <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={upload} data-testid="invoice-file-input" />
          <Button data-testid="upload-invoice-button" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="bg-[#D9381E] hover:bg-[#B91C1C] text-white gap-2">
            <Upload className="w-4 h-4" /> {uploading ? "Procesando OCR..." : "Subir factura"}
          </Button>
        </div>
      </div>

      <Card data-testid="invoices-filter-bar">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            <Input data-testid="invoices-search" placeholder="Buscar proveedor, nº, CIF..." value={filters.q}
              onChange={(e) => setF("q", e.target.value)} className="h-9 col-span-2" />
            <Select value={filters.status} onValueChange={(v) => setF("status", v)}>
              <SelectTrigger data-testid="invoices-status-filter" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="validated">Validadas</SelectItem>
                <SelectItem value="pending_review">Pendiente revisión</SelectItem>
                <SelectItem value="duplicate">Duplicadas</SelectItem>
                <SelectItem value="error">Error OCR</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.payment_status} onValueChange={(v) => setF("payment_status", v)}>
              <SelectTrigger data-testid="invoices-payment-filter" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Pago: todos</SelectItem>
                <SelectItem value="pendiente">Pendiente</SelectItem>
                <SelectItem value="parcial">Pago parcial</SelectItem>
                <SelectItem value="pagada">Pagada</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.branch || "all"} onValueChange={(v) => setF("branch", v === "all" ? "" : v)}>
              <SelectTrigger data-testid="invoices-branch-filter" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Sucursal: todas</SelectItem>
                {org.branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={monthSel} onValueChange={(v) => {
              const [f, t] = v === "all" ? ["", ""] : monthRangeFor(v);
              setFilters((p) => ({ ...p, date_from: f, date_to: t }));
            }}>
              <SelectTrigger data-testid="invoices-month-filter" className="h-9 w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Mes: todos</SelectItem>
                {monthOptions().map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input data-testid="invoices-date-from" type="date" value={filters.date_from} onChange={(e) => setF("date_from", e.target.value)} className="h-9" />
            <Input data-testid="invoices-date-to" type="date" value={filters.date_to} onChange={(e) => setF("date_to", e.target.value)} className="h-9" />
            <Select value={filters.contabilizada} onValueChange={(v) => setF("contabilizada", v)}>
              <SelectTrigger data-testid="invoices-contabilizada-filter" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Contab.: todas</SelectItem>
                <SelectItem value="true">Contabilizadas</SelectItem>
                <SelectItem value="false">Pendientes de contabilizar</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.currency} onValueChange={(v) => setF("currency", v)}>
              <SelectTrigger data-testid="invoices-currency-filter" className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Moneda: todas</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
                <SelectItem value="NO_EUR">Otra moneda (no EUR)</SelectItem>
              </SelectContent>
            </Select>
            <Input data-testid="invoices-registro-from" type="date" value={filters.registro_from} title="Registrada desde"
              onChange={(e) => setF("registro_from", e.target.value)} className="h-9" />
            <Input data-testid="invoices-registro-to" type="date" value={filters.registro_to} title="Registrada hasta"
              onChange={(e) => setF("registro_to", e.target.value)} className="h-9" />
          </div>
        </CardContent>
      </Card>

      {selectedIds.size > 0 && (
        <Card data-testid="invoices-bulk-bar" className="border-[#D9381E]/30 bg-orange-50/50">
          <CardContent className="p-3 flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium">{selectedIds.size} seleccionadas</span>
            {canGestoria && (
              <Button size="sm" variant="outline" data-testid="bulk-mark-contabilizadas" onClick={markBulkContabilizadas}>
                Marcar contabilizadas
              </Button>
            )}
            <Button size="sm" variant="outline" data-testid="bulk-download-zip" onClick={downloadZip} disabled={zipping} className="gap-1.5">
              <Download className="w-3.5 h-3.5" /> {zipping ? "Generando ZIP..." : "Descargar PDFs (ZIP)"}
            </Button>
            <Button size="sm" variant="ghost" data-testid="bulk-clear" onClick={() => setSelectedIds(new Set())}>Limpiar selección</Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold font-heading">{invoices.length} {invoices.length === 1 ? "factura" : "facturas"}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8">
                  <input type="checkbox" data-testid="invoices-select-all" className="accent-[#D9381E] w-4 h-4 cursor-pointer"
                    title="Seleccionar todas las del filtro actual"
                    checked={sortedInvoices.length > 0 && sortedInvoices.every((i) => selectedIds.has(i.id))}
                    onChange={toggleAllFiltered} />
                </TableHead>
                <InvSortHead label="Estado" sortKey="status" sort={invSort} clickSort={clickInvSort} testid="inv-sort-status" />
                <InvSortHead label="Pago" sortKey="payment_status" sort={invSort} clickSort={clickInvSort} testid="inv-sort-payment" />
                <InvSortHead label="Proveedor" sortKey="supplier_name" sort={invSort} clickSort={clickInvSort} testid="inv-sort-supplier" />
                <InvSortHead label="NIF/CIF" sortKey="supplier_tax_id" sort={invSort} clickSort={clickInvSort} testid="inv-sort-taxid" />
                <InvSortHead label="Tipo" sortKey="doc_kind" sort={invSort} clickSort={clickInvSort} testid="inv-sort-kind" />
                <InvSortHead label="Nº factura" sortKey="invoice_number" sort={invSort} clickSort={clickInvSort} testid="inv-sort-number" />
                <InvSortHead label="Fecha" sortKey="invoice_date" defDir="desc" sort={invSort} clickSort={clickInvSort} testid="inv-sort-date" />
                <InvSortHead label="Sucursal" sortKey="branch_name" sort={invSort} clickSort={clickInvSort} testid="inv-sort-branch" />
                <InvSortHead label="Base" sortKey="base_amount" defDir="desc" sort={invSort} clickSort={clickInvSort} testid="inv-sort-base" right />
                <InvSortHead label="IVA" sortKey="vat_amount" defDir="desc" sort={invSort} clickSort={clickInvSort} testid="inv-sort-vat" right />
                <TableHead className="text-right">IVA %</TableHead>
                <InvSortHead label="Total" sortKey="total" defDir="desc" sort={invSort} clickSort={clickInvSort} testid="inv-sort-total" right />
                <InvSortHead label="Conf." sortKey="overall_confidence" defDir="desc" sort={invSort} clickSort={clickInvSort} testid="inv-sort-conf" right />
                <InvSortHead label="Registro" sortKey="fecha_registro" defDir="desc" sort={invSort} clickSort={clickInvSort} testid="inv-sort-registro" />
                <TableHead data-testid="inv-head-contab">Contab.</TableHead>
                <TableHead>Observaciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedInvoices.map((inv) => {
                const st = STATUS[inv.status] || STATUS.processing;
                const pay = PAYMENT[inv.payment_status] || PAYMENT.pendiente;
                return (
                  <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}
                    className={`cursor-pointer ${inv.currency && inv.currency !== "EUR" ? "bg-sky-50 hover:bg-sky-100" : ""}`}
                    title={inv.currency && inv.currency !== "EUR" ? "Factura en otra moneda" : undefined}
                    onClick={() => openDetail(inv)}>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" data-testid={`invoice-select-${inv.id}`} className="accent-[#D9381E] w-4 h-4 cursor-pointer"
                        checked={selectedIds.has(inv.id)} onChange={() => toggleSelect(inv.id)} />
                    </TableCell>
                    <TableCell><Badge variant="outline" className={st.cls}>{st.label}</Badge></TableCell>
                    <TableCell><Badge variant="outline" className={pay.cls}>{pay.label}</Badge></TableCell>
                    <TableCell className="text-sm font-medium max-w-40 truncate">
                      <span className="inline-flex items-center gap-1">{inv.supplier_name || "—"}
                        {inv.has_attached_page && <Paperclip className="w-3.5 h-3.5 text-slate-400" data-testid={`attached-badge-${inv.id}`} />}</span>
                    </TableCell>
                    <TableCell className="font-num text-xs text-slate-500">{inv.supplier_tax_id || "—"}</TableCell>
                    <TableCell data-testid={`invoice-kind-${inv.id}`}>
                      {inv.doc_kind === "factura"
                        ? <Badge variant="outline" className="text-blue-700 border-blue-200 bg-blue-50">Factura fiscal</Badge>
                        : inv.doc_kind === "ticket"
                          ? <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50">Ticket</Badge>
                          : <span className="text-xs text-slate-400" title="Documento anterior a la clasificación automática — el admin puede corregirlo editando la factura">—</span>}
                    </TableCell>
                    <TableCell className="font-num text-xs">{inv.invoice_number || "—"}</TableCell>
                    <TableCell className="text-sm">{fmtDate(inv.invoice_date)}</TableCell>
                    <TableCell className="text-xs text-slate-500">{inv.branch_name || "—"}</TableCell>
                    <TableCell className="text-right font-num">{fmtEUR(inv.base_amount)}</TableCell>
                    <TableCell className="text-right font-num text-slate-500">{fmtEUR(inv.vat_amount)}</TableCell>
                    <TableCell className="text-right" data-testid={`invoice-vat-rates-${inv.id}`}><VatRatesCell inv={inv} /></TableCell>
                    <TableCell className="text-right font-num font-semibold">
                      {inv.currency && inv.currency !== "EUR"
                        ? <span className="inline-flex items-center justify-end gap-1">{inv.total != null ? Number(inv.total).toFixed(2) : "—"}
                            <Badge variant="outline" className="text-sky-700 border-sky-300 bg-sky-100 text-[10px] px-1" data-testid={`invoice-currency-${inv.id}`}>{inv.currency}</Badge></span>
                        : fmtEUR(inv.total)}
                    </TableCell>
                    <TableCell className="text-right"><ConfBadge value={inv.overall_confidence} /></TableCell>
                    <TableCell className="text-xs text-slate-500 whitespace-nowrap" data-testid={`invoice-registro-${inv.id}`}>{fmtRegistro(inv.fecha_registro)}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()} data-testid={`invoice-contab-${inv.id}`}>
                      {canGestoria
                        ? <input type="checkbox" className="accent-green-600 w-4 h-4 cursor-pointer" checked={!!inv.contabilizada}
                            title={inv.contabilizada ? `Contabilizada por ${inv.contabilizada_by} · ${fmtRegistro(inv.contabilizada_at)}` : "Marcar como contabilizada"}
                            onChange={() => toggleContabilizada(inv)} />
                        : (inv.contabilizada
                          ? <Badge variant="outline" className="text-green-700 border-green-200 bg-green-50">Sí</Badge>
                          : <span className="text-slate-300">—</span>)}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 max-w-32 truncate" title={inv.observaciones || ""} data-testid={`invoice-obs-${inv.id}`}>{inv.observaciones || "—"}</TableCell>
                  </TableRow>
                );
              })}
              {invoices.length === 0 && (
                <TableRow><TableCell colSpan={17} className="text-center py-10 text-slate-400 text-sm">
                  No hay facturas con estos filtros.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <InvoiceDrawer inv={selected} open={!!selected} onClose={closeDrawer}
        isAdmin={isAdmin} canReconcile={canReconcile} suppliers={suppliers} expenseCategories={expenseCategories}
        branches={org.branches} editForm={editForm} setEditForm={setEditForm} payments={payments}
        invCandidates={invCandidates} previewUrl={previewUrl} page2Url={page2Url} activePage={activePage}
        setActivePage={setActivePage} onOpenPage2={openPage2} onSave={saveEdit} onApprove={approve} onDelete={remove}
        onDetach={detachPage} onOpenAttachPage={() => setAttachOpen(true)} onReconcile={reconcileFromInvoice}
        onRefresh={() => openDetail(selected)} onChanged={load} />

      <DuplicatesDialog open={showDups} onClose={() => setShowDups(false)} onResolved={load} />
      <AttachPageDialog invoice={selected} open={attachOpen} onClose={() => setAttachOpen(false)}
        onAttached={() => { openDetail(selected); load(); }} />
    </div>
  );
}
